import { useEffect, useRef } from 'react';

/**
 * useModalAccessibility - A custom hook for accessible modals
 * 
 * Features:
 * 1. Focus Trap: Keeps keyboard focus inside the modal while open.
 * 2. Escape Key: Closes the modal when the Escape key is pressed.
 * 3. Backdrop Click: Closes the modal when clicking outside the modal content.
 * 
 * @param {boolean} isOpen - Whether the modal is currently open
 * @param {function} onClose - Function to call to close the modal
 * @returns {object} ref - A ref to attach to the modal's content container (not the backdrop)
 */
const useModalAccessibility = (isOpen, onClose) => {
    const modalRef = useRef(null);
    const previousFocusRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            // 1. Save currently focused element to restore later
            previousFocusRef.current = document.activeElement;

            // 2. Focus the modal container or the first focusable element
            // We use a small timeout to ensure the DOM is rendered
            const timer = setTimeout(() => {
                if (modalRef.current) {
                    // Try to find the first input, button, or focusable element
                    const focusableElement = modalRef.current.querySelector(
                        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
                    );
                    if (focusableElement) {
                        focusableElement.focus();
                    } else {
                        modalRef.current.focus();
                    }
                }
            }, 50);

            return () => clearTimeout(timer);
        } else {
            // Restore focus when modal closes
            if (previousFocusRef.current) {
                previousFocusRef.current.focus();
            }
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (e) => {
            // Escape Key to Close
            if (e.key === 'Escape') {
                onClose();
                return;
            }

            // Focus Trap Logic
            if (e.key === 'Tab') {
                if (!modalRef.current) return;

                const focusableElements = modalRef.current.querySelectorAll(
                    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
                );

                if (focusableElements.length === 0) return;

                const firstElement = focusableElements[0];
                const lastElement = focusableElements[focusableElements.length - 1];

                // Shift + Tab: If on first element, move to last
                if (e.shiftKey) {
                    if (document.activeElement === firstElement) {
                        e.preventDefault();
                        lastElement.focus();
                    }
                }
                // Tab: If on last element, move to first
                else {
                    if (document.activeElement === lastElement) {
                        e.preventDefault();
                        firstElement.focus();
                    }
                }
            }
        };

        // Backdrop Click Logic
        const handleClickOutside = (e) => {
            if (modalRef.current && !modalRef.current.contains(e.target)) {
                onClose();
            }
        };

        // Add event listeners
        document.addEventListener('keydown', handleKeyDown);
        // We attach click listener to document to catch clicks outside
        // Note: This requires the click event to bubble up. 
        // If the modal backdrop stops propagation, this might need adjustment.
        // However, usually attaching to document is safe for "click outside".
        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, onClose]);

    return modalRef;
};

export default useModalAccessibility;
